/**
 * Tags with fields (SPEC §5.2, §8). A tag's schema lives on the tag node itself, in the field
 * `sys:schema` (D25: the schema is on the target node, not in the syntax). Values live on the
 * tagged node: `props[field.id]`. Edits are per-field `node.props` ops (last writer wins per field,
 * SPEC §12); the whole schema is one field, so two devices editing one schema at once keep the
 * later version.
 */
import type { NodeId } from './ids.ts'
import { isUuid } from './validate.ts'

export const SCHEMA_FIELD = 'sys:schema'

export type FieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'datetime'
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'url'
  | 'email'
  | 'phone'
  | 'ref'

export const FIELD_TYPES: readonly FieldType[] = ['text', 'number', 'date', 'datetime', 'select', 'multiselect', 'checkbox', 'url', 'email', 'phone', 'ref']

export interface FieldDef {
  /** Stable key of the value in `props`; built-ins use readable ids (`task.due`). */
  readonly id: string
  readonly name: string
  readonly type: FieldType
  /** select / multiselect */
  readonly options?: readonly string[]
  /** ref: instances of this tag (null = any titled node) */
  readonly of?: NodeId | null
  /** ref: several values */
  readonly multi?: boolean
  readonly required?: boolean
  /** Shown elsewhere, not in the grid (a task's status is its prefix). */
  readonly hidden?: boolean
}

export interface Schema {
  readonly fields: readonly FieldDef[]
  /** Tags whose fields this one inherits (a field with the same id here overrides). */
  readonly extends: readonly NodeId[]
}

const FIELD_ID = /^[A-Za-z0-9_.:-]{1,64}$/

/** A schema from untrusted JSON: unknown types, bad ids and duplicates are dropped. */
export function readSchema(raw: unknown): Schema | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null
  const r = raw as { fields?: unknown; extends?: unknown }
  const fields: FieldDef[] = []
  const seen = new Set<string>()
  for (const f of Array.isArray(r.fields) ? r.fields : []) {
    if (f === null || typeof f !== 'object') continue
    const x = f as Record<string, unknown>
    if (typeof x.id !== 'string' || !FIELD_ID.test(x.id) || x.id.startsWith('sys:') || seen.has(x.id)) continue
    if (typeof x.type !== 'string' || !FIELD_TYPES.includes(x.type as FieldType)) continue
    const name = typeof x.name === 'string' && x.name.trim() ? x.name.trim().slice(0, 100) : x.id
    seen.add(x.id)
    const def: { -readonly [K in keyof FieldDef]: FieldDef[K] } = { id: x.id, name, type: x.type as FieldType }
    if (Array.isArray(x.options)) {
      def.options = [...new Set(x.options.filter((o): o is string => typeof o === 'string' && o.trim() !== '').map((o) => o.trim().slice(0, 100)))]
    }
    if (isUuid(x.of)) def.of = x.of
    if (x.multi === true) def.multi = true
    if (x.required === true) def.required = true
    if (x.hidden === true) def.hidden = true
    fields.push(def)
  }
  const ext = Array.isArray(r.extends) ? r.extends.filter(isUuid) : []
  return { fields, extends: [...new Set(ext)] }
}

/** A fresh field id for a user-defined field. */
export function newFieldId(rand: () => number): string {
  let s = 'f_'
  for (let i = 0; i < 8; i++) s += 'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(rand() * 36)]
  return s
}

const DATE = /^\d{4}-\d{2}-\d{2}$/
const DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})?$/

/**
 * A value for a field from user input, or undefined if it does not fit (the editor keeps the
 * input then). null clears the field.
 */
export function fieldValue(f: FieldDef, input: unknown): unknown {
  if (input === null || input === '' || (Array.isArray(input) && input.length === 0)) return null
  switch (f.type) {
    case 'text':
    case 'phone':
      return typeof input === 'string' ? input.trim().slice(0, 2000) || null : undefined
    case 'url':
      return typeof input === 'string' && /^(https?|mailto):/i.test(input.trim()) ? input.trim() : undefined
    case 'email':
      return typeof input === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim()) ? input.trim() : undefined
    case 'number': {
      const n = typeof input === 'number' ? input : typeof input === 'string' ? Number(input.replace(',', '.')) : NaN
      return Number.isFinite(n) ? n : undefined
    }
    case 'date':
      return typeof input === 'string' && DATE.test(input) ? input : undefined
    case 'datetime':
      return typeof input === 'string' && DATETIME.test(input) ? input : undefined
    case 'checkbox':
      return typeof input === 'boolean' ? input : undefined
    case 'select':
      return typeof input === 'string' && (f.options ?? []).includes(input) ? input : undefined
    case 'multiselect': {
      const xs = Array.isArray(input) ? input : [input]
      return xs.every((x) => typeof x === 'string' && (f.options ?? []).includes(x)) ? [...new Set(xs as string[])] : undefined
    }
    case 'ref': {
      const xs = Array.isArray(input) ? input : [input]
      if (!xs.every(isUuid)) return undefined
      return f.multi ? [...new Set(xs as string[])] : (xs[0] as string)
    }
  }
}

/** True if the node has a value for the field. */
export function hasValue(props: Readonly<Record<string, unknown>>, f: FieldDef): boolean {
  const v = props[f.id]
  return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)
}
